import React, { useState } from 'react';
import Header from '../components/Header';
import './RitualsPage.css';

function RitualsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'completed', 'pending', 'missed'

  // Mock rituals/habits data
  const rituals = [
    {
      id: 1,
      name: 'Meditación Matutina',
      description: 'Sesión de meditación de 10 minutos para comenzar el día con claridad mental',
      category: 'Bienestar',
      frequency: 'daily',
      time: '07:00',
      duration: 10,
      streak: 15,
      todayStatus: 'completed',
      weeklyProgress: [true, true, true, true, true, true, false],
      icon: '🧘',
      color: 'blue',
      tags: ['mindfulness', 'mañana', 'rutina']
    },
    {
      id: 2,
      name: 'Ejercicio Físico',
      description: 'Rutina de ejercicio cardiovascular y fuerza para mantener un buen estado físico',
      category: 'Salud',
      frequency: 'daily',
      time: '18:00',
      duration: 45,
      streak: 8,
      todayStatus: 'pending',
      weeklyProgress: [true, false, true, true, true, true, false],
      icon: '💪',
      color: 'green',
      tags: ['fitness', 'salud', 'energía']
    },
    {
      id: 3,
      name: 'Lectura Diaria',
      description: 'Leer al menos 20 páginas de un libro para desarrollo personal o profesional',
      category: 'Desarrollo',
      frequency: 'daily',
      time: '21:00',
      duration: 30,
      streak: 22,
      todayStatus: 'completed',
      weeklyProgress: [true, true, true, true, true, false, true],
      icon: '📚',
      color: 'purple',
      tags: ['conocimiento', 'crecimiento', 'noche']
    },
    {
      id: 4,
      name: 'Revisión de Objetivos',
      description: 'Evaluar el progreso semanal y ajustar metas para la siguiente semana',
      category: 'Productividad',
      frequency: 'weekly',
      time: '10:00',
      duration: 60,
      streak: 4,
      todayStatus: 'pending',
      weeklyProgress: [false, false, false, false, false, false, true],
      icon: '🎯',
      color: 'orange',
      tags: ['objetivos', 'planificación', 'reflexión']
    },
    {
      id: 5,
      name: 'Llamada Familiar',
      description: 'Conexión semanal con familia para mantener vínculos afectivos fuertes',
      category: 'Relaciones',
      frequency: 'weekly',
      time: '16:00',
      duration: 30,
      streak: 12,
      todayStatus: 'missed',
      weeklyProgress: [false, false, false, false, false, false, false],
      icon: '👨‍👩‍👧‍👦',
      color: 'pink',
      tags: ['familia', 'conexión', 'afecto']
    },
    {
      id: 6,
      name: 'Organización del Espacio',
      description: 'Ordenar y limpiar el área de trabajo para mantener un ambiente productivo',
      category: 'Organización',
      frequency: 'daily',
      time: '08:30',
      duration: 15,
      streak: 5,
      todayStatus: 'completed',
      weeklyProgress: [true, true, false, true, true, false, true],
      icon: '🧹',
      color: 'gray',
      tags: ['orden', 'limpieza', 'productividad']
    }
  ];

  // Filter rituals
  const filteredRituals = rituals.filter(ritual => {
    const matchesSearch = ritual.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ritual.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ritual.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ritual.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesFilter = activeFilter === 'all' || ritual.todayStatus === activeFilter;
    
    return matchesSearch && matchesFilter;
  });

  // Calculate stats
  const stats = {
    total: rituals.length,
    completed: rituals.filter(r => r.todayStatus === 'completed').length,
    pending: rituals.filter(r => r.todayStatus === 'pending').length,
    missed: rituals.filter(r => r.todayStatus === 'missed').length,
    avgStreak: Math.round(rituals.reduce((sum, r) => sum + r.streak, 0) / rituals.length)
  };

  const handleRitualAction = (ritualId, action) => {
    console.log(`${action} ritual:`, ritualId);
    // Implement ritual actions
  };

  const formatTime = (time) => {
    return time;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'green';
      case 'pending': return 'orange';
      case 'missed': return 'red';
      default: return 'gray';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return '✅';
      case 'pending': return '⏳';
      case 'missed': return '❌';
      default: return '⚪';
    }
  };

  const getProgressPercentage = (weeklyProgress) => {
    const completed = weeklyProgress.filter(day => day).length;
    return Math.round((completed / weeklyProgress.length) * 100);
  };

  return (
    <div className="rituals-page">
      <Header 
        currentPage="rituals" 
        showSearch 
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showAddButton 
        onAddNew={() => console.log('Nuevo ritual')}
      />

      <div className="rituals-content">
        {/* Rituals Stats */}
        <div className="rituals-stats">
          <div className="stat-card total">
            <div className="stat-icon">🔄</div>
            <div className="stat-content">
              <h3>{stats.total}</h3>
              <p>Total Rituales</p>
            </div>
          </div>
          <div className="stat-card completed">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <h3>{stats.completed}</h3>
              <p>Completados Hoy</p>
            </div>
          </div>
          <div className="stat-card pending">
            <div className="stat-icon">⏳</div>
            <div className="stat-content">
              <h3>{stats.pending}</h3>
              <p>Pendientes</p>
            </div>
          </div>
          <div className="stat-card streak">
            <div className="stat-icon">🔥</div>
            <div className="stat-content">
              <h3>{stats.avgStreak}</h3>
              <p>Racha Promedio</p>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="rituals-filters">
          <div className="filter-tabs">
            <button 
              className={`filter-tab ${activeFilter === 'all' ? 'active' : ''}`}
              onClick={() => setActiveFilter('all')}
            >
              🌐 Todos ({stats.total})
            </button>
            <button 
              className={`filter-tab ${activeFilter === 'completed' ? 'active' : ''}`}
              onClick={() => setActiveFilter('completed')}
            >
              ✅ Completados ({stats.completed})
            </button>
            <button 
              className={`filter-tab ${activeFilter === 'pending' ? 'active' : ''}`}
              onClick={() => setActiveFilter('pending')}
            >
              ⏳ Pendientes ({stats.pending})
            </button>
            <button 
              className={`filter-tab ${activeFilter === 'missed' ? 'active' : ''}`}
              onClick={() => setActiveFilter('missed')}
            >
              ❌ Perdidos ({stats.missed})
            </button>
          </div>
          <div className="filter-actions">
            <button className="btn-secondary">Vista Calendario</button>
            <button className="btn-secondary">Estadísticas</button>
            <button className="btn-secondary">Exportar</button>
          </div>
        </div>

        {/* Rituals List */}
        <div className="rituals-section">
          <div className="section-header">
            <h2>Mis Rituales ({filteredRituals.length})</h2>
            <div className="progress-indicator">
              <span className="daily-progress">
                🎯 Progreso Diario: {Math.round((stats.completed / stats.total) * 100)}%
              </span>
            </div>
          </div>

          <div className="rituals-list">
            {filteredRituals.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🔄</div>
                <h3>No se encontraron rituales</h3>
                <p>Intenta ajustar los términos de búsqueda o crear un nuevo ritual</p>
              </div>
            ) : (
              filteredRituals.map((ritual) => (
                <div key={ritual.id} className={`ritual-card ${getStatusColor(ritual.todayStatus)}`}>
                  <div className="ritual-header">
                    <div className="ritual-info">
                      <div className="ritual-title-row">
                        <div className="ritual-icon">
                          {ritual.icon}
                        </div>
                        <h4 className="ritual-name">{ritual.name}</h4>
                        <div className="ritual-status">
                          <span className={`status-badge ${getStatusColor(ritual.todayStatus)}`}>
                            {getStatusIcon(ritual.todayStatus)} {ritual.todayStatus.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <p className="ritual-description">{ritual.description}</p>
                      <div className="ritual-details">
                        <span className="detail-item">⏰ {formatTime(ritual.time)}</span>
                        <span className="detail-item">⌛ {ritual.duration} min</span>
                        <span className="detail-item">🔥 {ritual.streak} días</span>
                        <span className="detail-item">📊 {ritual.category}</span>
                        <span className="detail-item">📅 {ritual.frequency}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="ritual-body">
                    <div className="weekly-progress">
                      <span className="progress-label">Progreso Semanal:</span>
                      <div className="progress-dots">
                        {ritual.weeklyProgress.map((completed, index) => (
                          <div 
                            key={index} 
                            className={`progress-dot ${completed ? 'completed' : 'missed'}`}
                            title={`${['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'][index]}: ${completed ? 'Completado' : 'Perdido'}`}
                          >
                          </div>
                        ))}
                      </div>
                      <span className="progress-percentage">{getProgressPercentage(ritual.weeklyProgress)}%</span>
                    </div>
                    
                    <div className="ritual-tags">
                      {ritual.tags.map((tag, index) => (
                        <span key={index} className="ritual-tag">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="ritual-actions">
                    {ritual.todayStatus === 'pending' ? (
                      <button 
                        className="btn-action success"
                        onClick={() => handleRitualAction(ritual.id, 'complete')}
                      >
                        ✅ Marcar Completado
                      </button>
                    ) : ritual.todayStatus === 'missed' ? (
                      <button 
                        className="btn-action warning"
                        onClick={() => handleRitualAction(ritual.id, 'recover')}
                      >
                        🔄 Recuperar
                      </button>
                    ) : (
                      <button 
                        className="btn-action secondary"
                        onClick={() => handleRitualAction(ritual.id, 'undo')}
                      >
                        ↩️ Deshacer
                      </button>
                    )}
                    <button 
                      className="btn-action secondary"
                      onClick={() => handleRitualAction(ritual.id, 'edit')}
                    >
                      ✏️ Editar
                    </button>
                    <button 
                      className="btn-action secondary"
                      onClick={() => handleRitualAction(ritual.id, 'stats')}
                    >
                      📊 Estadísticas
                    </button>
                    <button 
                      className="btn-action secondary"
                      onClick={() => handleRitualAction(ritual.id, 'skip')}
                    >
                      ⏭️ Saltar Hoy
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="quick-actions-section">
          <h2>Herramientas de Hábitos</h2>
          <div className="quick-actions-grid">
            <div className="quick-action-card">
              <div className="action-icon">📈</div>
              <h3>Análisis de Hábitos</h3>
              <p>Revisa tendencias y patrones de cumplimiento</p>
              <button className="btn-primary">Ver Analytics</button>
            </div>
            <div className="quick-action-card">
              <div className="action-icon">🎯</div>
              <h3>Plantillas</h3>
              <p>Crea rituales desde plantillas predefinidas</p>
              <button className="btn-primary">Explorar</button>
            </div>
            <div className="quick-action-card">
              <div className="action-icon">🔔</div>
              <h3>Recordatorios</h3>
              <p>Configura notificaciones para tus rituales</p>
              <button className="btn-primary">Configurar</button>
            </div>
            <div className="quick-action-card">
              <div className="action-icon">🏆</div>
              <h3>Logros</h3>
              <p>Celebra tus rachas y metas alcanzadas</p>
              <button className="btn-primary">Ver Logros</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RitualsPage;
